import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container } from "@material-ui/core";
import PageBackground from "../../organisms/PageBackground";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { PageTitle } from "@thrivetribe/gloji.ui.molecules.page-title";
import { MINT } from "@thrivetribe/gloji.enums.brand-colors";
import { Helmet } from "react-helmet";
import { myProgressBg } from "../../../assets/img/bg";
import { useDispatch, useSelector } from "react-redux";
import { get_all_weights } from "../../../store/actions/getAllWeights";
import useUser from "../../hooks/useUser";
import AlreadyLoggedWeightToday from "../../atoms/AlreadyLoggedWeightToday";
import { CustomModal, ModalContent, ModalTitle, ModalCloseButton } from "../../organisms/CustomModal";
import Toast from "../../atoms/Toast";
import "./MyProgress.scss";
import LogWeightForm from "../../molecules/LogWeightForm";
import { log_weight_error_reset, log_weight_success_reset } from "../../../store/actions/postWeight";
import GraphWithHeaderFooter from "@thrivetribe/gloji.ui.organisms.graph-with-header-footer";
import { weightTypes } from "../../../enums/weightTypes";
import useUserId from "../../hooks/useUserId";
import { useGetJourneyByIdQuery } from "../../../redux/profileApi";
import { skipToken } from "@reduxjs/toolkit/dist/query/react";
import { Journey } from "../../../types/ProfileViewModel";

interface GetJourneyByIdQueryModel {
  userId: string;
  journeyId: string;
}

const FollowupWeight = () => {
  const { journeyId } = useParams();
  const dispatch = useDispatch();
  const [user] = useUser();
  const [userId] = useUserId();
  const [weightLogOpen, setWeightLogOpen] = useState(false);
  const [weightLogSuccessfulModalOpen, setWeightLogSuccessfulModalOpen] = useState(false);
  const [weightLogSuccessful, setWeightLogSuccessful] = useState(false);
  const [journey, setJourney] = useState<Journey>();
  const [journeyReady, setJourneyReady] = useState(false);
  const [programmeWeights, setProgrammeWeights] = useState([]);
  const allWeights = useSelector((state: any) => state.allWeights);
  const [displayWeightWarning, setDisplayWeightWarning] = useState(false);

  const getJourneyByIdQuery: GetJourneyByIdQueryModel = { userId, journeyId };
  const journeyQuery = useGetJourneyByIdQuery(getJourneyByIdQuery ?? skipToken);

  useEffect(() => {
    if (!journeyQuery?.isSuccess || journeyQuery?.isUninitialized) {
      return;
    }
    setJourney(journeyQuery.data.journey);
    setJourneyReady(true);
  }, [journeyQuery.data, journeyQuery?.isSuccess, journeyQuery?.isUninitialized]);

  useEffect(() => {
    if (journeyReady && journey) {
      dispatch(get_all_weights(userId, journey?.id));
    }
  }, [dispatch, journey, journeyReady, userId]);

  useEffect(() => {
    if (allWeights?.data?.weights === undefined || allWeights?.data?.weights.length === 0) return;

    const programmeWeights = allWeights.data.weights.filter(
      weight => weight.weightType === weightTypes.PROGRAMME || weight.weightType === null
    );
    setProgrammeWeights(programmeWeights);
  }, [allWeights.data]);

  // Modal functions
  const handleShowModal = () => {
    setWeightLogOpen(true);
    document.getElementById("root").classList.add("modal-active");
  };

  const handleLogWeight = async () => {
    return handleShowModal();
  };

  const handleModalClose = () => {
    setWeightLogOpen(false);
    setWeightLogSuccessfulModalOpen(false);
    setDisplayWeightWarning(false);

    document.getElementById("root").classList.remove("modal-active");
    dispatch(get_all_weights(userId, journey?.id));
    dispatch(log_weight_success_reset());
    dispatch(log_weight_error_reset());
  };

  const showSuccessMessage = () => {
    setWeightLogSuccessful(true);
    setWeightLogSuccessfulModalOpen(true);
  };

  const handleSuccessMessageClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setWeightLogSuccessful(false);
    setDisplayWeightWarning(false);
  };

  const handleReturnHome = () => {
    handleModalClose();
    return (window.location.href = "/");
  };

  if (!journeyReady || !journey) {
    return <></>;
  }

  return (
    <PageBackground bgImage="">
      <Helmet>
        <title>Followup Weight | My progress | Gloji</title>
      </Helmet>
      <section className="chart-section__container" style={{ backgroundImage: `url(${myProgressBg})` }}>
        <Container>
          <PageTitle color={MINT}>My progress</PageTitle>
          <GraphWithHeaderFooter
            dataLoading={allWeights.loading}
            chartData={programmeWeights}
            isAdvisorView={false}
            firstName={user.given_name}
            handleLogWeight={() => handleLogWeight()}
          />
        </Container>
        {allWeights.loading === false && journeyReady && (
          <CustomModal
            open={weightLogOpen}
            onClose={handleModalClose}
            id="logWeightModal"
            modalTitleId="logWeightModalTitle"
            modalDescriptionId=""
            aria-labelledby={"logWeightModalTitle"}
          >
            <ModalContent id="logWeightModalContent" modalTitleId="logWeightModalTitle" modalDescriptionId="">
              <ModalTitle id="logWeightModalTitle">Log my weight</ModalTitle>
              {allWeights.data.weights !== undefined && (
                <AlreadyLoggedWeightToday allWeights={allWeights.data.weights} />
              )}

              {displayWeightWarning && (
                <div className="logging-status__error">
                  <p id="low-weight-warning">Can you double check? That doesn't look quite right.</p>
                </div>
              )}

              <LogWeightForm
                allWeights={allWeights.data.weights}
                closeModal={handleModalClose}
                onSuccess={showSuccessMessage}
                setDisplayWeightWarning={setDisplayWeightWarning}
                journeyId={journey.id}
              />
            </ModalContent>
            <ModalCloseButton onClick={handleModalClose} />
          </CustomModal>
        )}

        <CustomModal
          open={weightLogSuccessfulModalOpen}
          onClose={handleModalClose}
          id="readyToGoModal"
          modalTitleId="readyToGoModalTitle"
          modalDescriptionId="readyToGoModalDescription"
          aria-labelledby={"readyToGoModalTitle"}
        >
          <ModalContent id="readyToGoModalContent">
            <div className="celebration-img-container"></div>
            <ModalTitle id="readyToGoModalTitle">Your weight was successfully logged!</ModalTitle>
            <p className="modal-text" id="readyToGoModalDescription">
              What would you like to do?
            </p>
            <div id="readyToGoModalButtons">
              <Button
                className="complete-button"
                variant="contained"
                color="primary"
                onClick={handleModalClose}
              >
                View logged weights
              </Button>
              <Button
                className="complete-button"
                variant="contained"
                color="secondary"
                onClick={handleReturnHome}
              >
                Return to home
              </Button>
            </div>
          </ModalContent>
        </CustomModal>

        <Toast show={weightLogSuccessful} type="success" handleClose={handleSuccessMessageClose}>
          Your weight was successfully logged!
        </Toast>
      </section>
    </PageBackground>
  );
};

export default FollowupWeight;
